'use client'

import React, { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'

interface ShareButtonProps {
  title: string
  className?: string
}

export function ShareButton({ title, className }: ShareButtonProps) {
  const [copied, setCopied] = useState(false)
  const t = useTranslations('product')

  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => {
        setCopied(false)
      }, 2000)
      return () => clearTimeout(timeout)
    }
  }, [copied])

  const handleShare = async () => {
    const url = window.location.href

    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          url: url,
        })
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          copyToClipboard(url)
        }
      }
    } else {
      copyToClipboard(url)
    }
  }

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch (err) {
      console.error('Failed to copy: ', err)
    }
  }

  // Fallbacks if t() is missing for any reason
  let defaultLabel = 'Teilen'
  let linkCopiedLabel = 'Link kopiert!'
  try {
    defaultLabel = t('share')
    // We added 'share' to messages, so it should exist
  } catch (e) {}

  const tooltipText = copied ? linkCopiedLabel : defaultLabel

  return (
    <div className={`relative group/share ${className || ''}`}>
      <Button
        type="button"
        variant="neon-white"
        className="font-mono uppercase tracking-wider rounded-lg dark:bg-black dark:text-[var(--neon-orange)] dark:border-white/10 w-10 h-10 flex-shrink-0 flex items-center justify-center !p-0"
        onClick={handleShare}
        aria-label={tooltipText}
      >
        {copied ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        ) : (
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="18" 
            height="18" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
            <polyline points="16 6 12 2 8 6"/>
            <line x1="12" y1="2" x2="12" y2="15"/>
          </svg>
        )}
      </Button>

      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs font-mono rounded opacity-0 group-hover/share:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 dark:bg-white dark:text-black">
        {tooltipText}
        {/* Tooltip Arrow */}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-black dark:border-t-white"></div>
      </div>
    </div>
  )
}
